import React from "react";
import DecorSelectorInput from "../../common/inputs/DecorSelectorInput";
import IBoardData from "../../database/interfaces/IBoardData";
import IFiltersTypeDescriptor from "../../database/interfaces/paramScript/IFiltersDescriptor";
import { DatabaseContext } from "../../database/DatabaseHandler";
import Stack from "@mui/material/Stack";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import LayersIcon from '@mui/icons-material/Layers';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import { createCdnThumbnailUrl } from "../../../config/cdn";
import EdgeBorderedAvatar from "../common/EdgeBorderedAvatar";
import i18n from "../../i18n";
import BoardDecorSelectorInput from "../../common/inputs/BoardDecorSelectorInput";

interface IBoardContentProps {
    board: IBoardData;
    format: string | null;
    filters: IFiltersTypeDescriptor | null;
    tooltip: string;
    patternDirection: string;
    duplungChecked: boolean;
    isDuplung: boolean;
    editable: boolean;
    readOnly: boolean;
    notFound: boolean;
    showPatternDirection?: boolean;
    edges?: { top: boolean; bottom: boolean; left: boolean; right: boolean };
    /** A nem élzárható oldalak (pl. munkalapnál a front, ahol nincs Top) — szaggatott keret a thumbnailen. */
    unavailableEdges?: { top: boolean; bottom: boolean; left: boolean; right: boolean };
    onDecorChange: (newId: string | null) => void;
    onPatternDirectionChange: (value: string) => void;
    onDuplungChange?: (newValue: boolean) => void;
}

export const CALC_METHOD_ICONS: Record<string, string> = {
    Full: "▢",
    Half: "◧",
    Quarter: "◱",
    SquareMeter: "⊞"
};

const BoardContent: React.FC<IBoardContentProps> = (props) => {
    const dataContext = React.useContext(DatabaseContext);
    /**
     * A választó-lista felirata. A descriptor `board.format` sablonjából jön, ha meg van adva —
     * helyőrzők: {name} {thickness} {width} {height} {price} {pricePerMeter}. Sablon nélkül a korábbi,
     * alapértelmezett felirat marad (név - vastagság - egységár), tehát meglévő dekor-választók
     * változatlanok. Így pl. a munkalapoknál a lemez SZÉLESSÉGE is kiírható — kód-módosítás nélkül,
     * adatból.
     *
     * ÁR-EGYSÉGEK: az adatbázis egységesen NÉGYZETMÉTER-árat tárol (uc_boardprices.UnitPrice ==
     * SheetPrice / tábla-terület, minden laptípusra). A `{price}` ezt adja. A `{pricePerMeter}` ebből
     * LEVEZETETT folyóméter-ár (Ft/m² × lemez-szélesség), mert a munkalapokat kereskedelmileg
     * folyóméterben árazzák. Hogy melyik jelenjen meg, azt a DESCRIPTOR dönti el, nem ez a komponens:
     * a view nem tudja (és ne is tudja), melyik terméknél melyik egység a helyes.
     */
    const nameFormatter = (board: IBoardData) => {
        try {
            const price = board?.GrossUnitPrice ?? 'N/A';
            // Hiányzó ár vagy szélesség esetén nincs mit levezetni — ugyanaz az 'N/A', mint a m²-árnál.
            const pricePerMeter = (board?.GrossUnitPrice != null && board?.SheetWidth)
                ? Math.round(board.GrossUnitPrice * board.SheetWidth / 1000)
                : 'N/A';
            if (props.format) {
                const values: Record<string, string | number> = {
                    name: board.Name,
                    thickness: board.SheetThickness,
                    width: board.SheetWidth,
                    height: board.SheetHeight,
                    price: price,
                    pricePerMeter: pricePerMeter,
                };
                return props.format.replace(/\{(\w+)\}/g, (match, key) =>
                    key in values ? String(values[key]) : match
                );
            }
            return `${board.Name} - ${board.SheetThickness}mm (${price} Ft/m²)`;
        } catch (error) {
            console.error("Error formatting board name:", error);
            return board.Name;
        }
    };

    const boardFilter = (board: IBoardData) => {
        if (!props.filters) return true;
        let retVal = true;
        if (props.filters.duplungable !== undefined) {
            retVal = (props.filters.duplungable ? board.Duplungable == 1 : board.Duplungable == 0);
        }
        if (retVal && props.filters.worktop !== undefined) {
            retVal = retVal && (props.filters.worktop ? board.IsWorkTop === 1 : board.IsWorkTop === 0);
        }
        // if (retVal && props.filters.worktop !== undefined) {
        //     // IsWorkTop lehet number (0/1) VAGY boolean (és hiányozhat is) → truthy check.
        //     // A strict === 1 a boolean true-val érkező worktopokat kiejtené a listából!
        //     retVal = retVal && (props.filters.worktop ? !!board.IsWorkTop : !board.IsWorkTop);
        // }
        if (retVal && props.filters.edgebandable !== undefined) {
            retVal = retVal && (props.filters.edgebandable ? board.EdgeBandable == 1 : board.EdgeBandable == 0);
        }
        if (retVal && props.filters.oriented !== undefined) {
            retVal = retVal && (props.filters.oriented ? board.Oriented == 1 : board.Oriented == 0);
        }
        if (retVal && props.filters.thickness !== undefined) {
            retVal = retVal && (board.SheetThickness >= props.filters.thickness.min && board.SheetThickness <= props.filters.thickness.max);
        }

        if (retVal && props.filters.core !== undefined && props.filters.core.length > 0 && board.Core) {
            retVal = retVal && props.filters.core.includes(board.Core);
        }
        return retVal;
    };

    const thumbnailUrl: string = createCdnThumbnailUrl(props.board.ProductImage);

    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <Badge
                invisible={props.readOnly || props.isDuplung || props.board.Duplungable == 0}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                    <IconButton
                        tabIndex={-1}
                        disabled={!props.editable || props.isDuplung || props.board.Duplungable == 0}
                        onClick={() => props.onDuplungChange && props.onDuplungChange(!props.duplungChecked)}
                        sx={{
                            color: '#424242',
                            backgroundColor: '#ffffff',
                            border: '1px solid #c0c0c0',
                            padding: '0.125rem',
                            '&:hover': {
                                backgroundColor: '#e0e0e0',
                            },
                        }}
                    >
                        {props.duplungChecked ? <LayersClearIcon  /> : <LayersIcon />}
                    </IconButton>
                }
            >
                <EdgeBorderedAvatar
                    alt={props.board.ID}
                    thumbnailUrl={thumbnailUrl}
                    edges={props.edges}
                    unavailable={props.unavailableEdges}
                    rotated={props.board.Oriented == 1 && props.patternDirection === "Cross"}
                />
            </Badge>

            <Stack direction="column" spacing={1} alignItems="flex-start" width="100%">
                <BoardDecorSelectorInput
                    value={props.board.ID}
                    // autoSelectFallback={true}
                    editable={props.editable}
                    readOnly={props.readOnly}
                    notFound={props.notFound}
                    options={dataContext.boardData?.children()
                        .filter(boardFilter)
                        // .map(board => ({ id: board.ID, name: nameFormatter(board) }))
                        .map(board => ({
                            id: board.ID,
                            name: nameFormatter(board),
                            ...(board.CalcMethod && CALC_METHOD_ICONS[board.CalcMethod]
                                ? {
                                    icon: CALC_METHOD_ICONS[board.CalcMethod],
                                    tooltip: i18n.t(`elements:calcMethod.${board.CalcMethod}`),
                                }
                                : {}),
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name)) || []
                    }
                    tooltip={props.tooltip}
                    onChange={props.onDecorChange}
                />

                {props.board.Oriented == 1 && Boolean(props.showPatternDirection ?? true) && (
                    props.readOnly
                        ? <span style={{ whiteSpace: 'nowrap' }}>
                            {(() => {
                                switch (props.patternDirection) {
                                    case "Straight": return "Egyenes";
                                    case "Cross": return "Kereszt";
                                    case "DontCare": return "Nem számít";
                                    default: return "N/A";
                                }
                            })()}
                          </span>
                        : <Select
                            size="small"
                            value={props.patternDirection}
                            disabled={!props.editable}
                            onChange={(event) => props.onPatternDirectionChange(event.target.value)}
                        >
                            <MenuItem value="Straight">Egyenes</MenuItem>
                            <MenuItem value="Cross">Kereszt</MenuItem>
                            <MenuItem value="DontCare">Nem számít</MenuItem>
                        </Select>
                )}
            </Stack>
        </Stack>
    );
};

export default BoardContent;